import { DateTime } from 'luxon'
import { useIntl } from 'react-intl'
import ButtonComponent from '../../../../../../components/stories/atoms/button'
import {
  buttonTypeOptions,
  buttonVariantOptions
} from '../../../../../../components/stories/atoms/button/constants'
import CheckboxComponent from '../../../../../../components/stories/atoms/checkboxes'
import { checkboxSizeOptions } from '../../../../../../components/stories/atoms/checkboxes/constants'
import { Settings } from '../../../../../../components/stories/atoms/icons/core'
import {
  Folder,
  Project
} from '../../../../../../components/stories/atoms/icons/entities'
import {
  ChevronDown,
  ChevronRight,
  Confirm
} from '../../../../../../components/stories/atoms/icons/essentials'
import { iconSizeOptions } from '../../../../../../components/stories/atoms/icons/icons-sizes'
import colorsOptions from '../../../../../../components/stories/fundamentals/colors-styles/constants'
import {
  textStyleSizeOptions,
  textStyleWeightOptions
} from '../../../../../../components/stories/fundamentals/text-styles/constants'
import { TableRowText } from '../../../../../../components/stories/organisms/table/components/row/components/table-row-text'
import { TableRowPathCell } from '../../../../../../components/stories/organisms/table/components/row/components/table-row-path-cell'
import {
  RowText,
  RowTextContainer
} from '../../../../../../components/stories/organisms/table/components/row/styled-components'
import TableRowCell from '../../../../../../components/stories/organisms/table/components/row/table-row-cell'
import { RowLeadingContainer } from '../../../../../../components/stories/organisms/table/styled-components'
import {
  tableColumnSizeOptions,
  tableHeaderOrientationOptions,
  tableHeaderTypeOptions
} from '../../../../../../components/stories/organisms/table/constants'
import {
  projectTreeItemTypeOptions,
  projectTreeSortByOptions
} from '../../../../../../react-query/project/query/use-get-project-tree-query'
import { useGetProjectPathQuery } from '../../../../../../react-query/project/query/use-get-project-path-query'
import { DragNDrop } from '../../../../../../components/stories/atoms/icons/table'
import SkeletonComponent from '../../../../../../components/stories/atoms/skeleton'
import BadgesComponent from '../../../../../../components/stories/atoms/badges'
import {
  badgeColorOptions,
  badgeSizeOptions,
  badgesVariantOptions
} from '../../../../../../components/stories/atoms/badges/constants'
import CompanyProjectsContextMenu from '../../../company-projects-context-menu'

const projectMenuTableSkeletonRowCount = 8

const projectMenuFormatMessageFallback = ({ id, defaultMessage }, values) => {
  if (id === 'projects_items') {
    return `${values?.entries ?? 0} ${values?.entries === 1 ? 'item' : 'items'}`
  }

  return defaultMessage ?? id
}

const getItemType = (item) =>
  item.type === projectTreeItemTypeOptions.FOLDER
    ? projectTreeItemTypeOptions.FOLDER
    : projectTreeItemTypeOptions.PROJECT

const formatLastActivity = (lastActivity) => {
  if (!lastActivity) return ''

  const parsedDate = DateTime.fromISO(lastActivity)

  if (!parsedDate.isValid) return lastActivity

  return parsedDate.toRelative()
}

const renderTableCellSkeleton = () => (
  <SkeletonComponent
    isLoading
    skeletonShape={{
      width: '100%',
      height: '16px',
      borderRadius: '6px'
    }}
  />
)

const renderTitleCellSkeleton = () => (
  <TableRowCell orientation={tableHeaderOrientationOptions.LEFT}>
    {renderTableCellSkeleton()}
  </TableRowCell>
)

const ProjectPathCell = ({ projectId }) => {
  const { formatMessage } = useIntl()
  const {
    data: path,
    isLoading,
    isError
  } = useGetProjectPathQuery({
    projectId
  })

  const displayPath =
    path === '/'
      ? formatMessage({ id: 'main_folder', defaultMessage: 'Main folder' })
      : path

  return (
    <TableRowPathCell
      path={displayPath}
      isLoading={isLoading}
      isError={isError}
    />
  )
}

export const buildDataSkeleton = (
  rowCount = projectMenuTableSkeletonRowCount
) =>
  Array.from({ length: rowCount }, (_, index) => ({
    id: `project-menu-table-skeleton-${index}`,
    name: '',
    type: projectTreeItemTypeOptions.PROJECT,
    active: true,
    parentId: null,
    ancestorFolderIds: [],
    items: 0,
    permissions: [],
    lastActivity: null,
    lastActivityLabel: '',
    isDraggable: false,
    isDroppable: false,
    isSkeleton: true
  }))

export const buildData = (
  data,
  {
    childrenByFolderId = new Map(),
    parentId = null,
    ancestorFolderIds = []
  } = {}
) => {
  if (!data) return []

  return data.map((item) => {
    const type = getItemType(item)
    const isFolder = type === projectTreeItemTypeOptions.FOLDER
    const itemParentId = item.parentId ?? parentId
    const folderChildren = childrenByFolderId.get(item.id)

    return {
      id: item.id,
      name: item.name,
      type,
      active: item.active ?? true,
      parentId: itemParentId,
      ancestorFolderIds,
      items: item.items ?? 0,
      permissions: item.permissions ?? [],
      lastActivity: item.lastActivity,
      lastActivityLabel: formatLastActivity(item.lastActivity),
      isDraggable: true,
      isDroppable: isFolder,
      subRows:
        isFolder && folderChildren
          ? buildData(folderChildren, {
              childrenByFolderId,
              parentId: item.id,
              ancestorFolderIds: [...ancestorFolderIds, item.id]
            })
          : undefined
    }
  })
}

export const buildColumnsDefault = ({
  formatMessage = projectMenuFormatMessageFallback,
  userPermissions = [],
  isSearching = false,
  hasFolderRow = false,
  // project handlers
  onCopyProject = null,
  onDeactivateItem = null,
  onMoveItem = null,
  onOpenProject = () => {},
  onOpenProjectSettings = () => {},

  // folder handlers
  onCreateProject,
  onCreateFolder,
  onRenameFolder
} = {}) => [
  {
    id: 'drag',
    accessorFn: (row) => row.isDraggable,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: (info) => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        {info.getValue() && <DragNDrop size={iconSizeOptions['ICON-S']} />}
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.HANDLE,
      orientation: tableHeaderOrientationOptions.CENTER,
      showIcon: true
    }
  },
  {
    id: projectTreeSortByOptions.TITLE,
    accessorKey: 'name',
    header: formatMessage({ id: 'title', defaultMessage: 'Title' }),
    cell: ({ row }) => {
      const isFolder = row.original.type === projectTreeItemTypeOptions.FOLDER
      const isProject = row.original.type === projectTreeItemTypeOptions.PROJECT
      const isActive = row.original.active
      const handleToggleFolder = row.getToggleExpandedHandler()
      const handleOpenProject = () => onOpenProject(row.original.id)

      return (
        <TableRowCell
          orientation={tableHeaderOrientationOptions.LEFT}
          style={{
            paddingLeft: `${row.depth * 40}px`
          }}
        >
          {isFolder && (
            <RowLeadingContainer
              onClick={handleToggleFolder}
              style={{ cursor: 'pointer' }}
            >
              {row.getIsExpanded() ? (
                <ChevronDown size={iconSizeOptions['ICON-S']} />
              ) : (
                <ChevronRight size={iconSizeOptions['ICON-S']} />
              )}
            </RowLeadingContainer>
          )}
          <TableRowText
            leadingComponent={
              isFolder ? (
                <Folder size={iconSizeOptions['ICON-L']} />
              ) : (
                <Project
                  size={iconSizeOptions['ICON-L']}
                  stroke={
                    isActive
                      ? colorsOptions.greys['grey-90']
                      : colorsOptions.greys['grey-40']
                  }
                  style={hasFolderRow ? { marginLeft: '3rem' } : undefined}
                />
              )
            }
            mainText={
              <RowText
                as={isProject ? 'button' : 'span'}
                type={isProject ? 'button' : undefined}
                onClick={isProject ? handleOpenProject : undefined}
                weight={textStyleWeightOptions['TEXT-600']}
                size={textStyleSizeOptions['TEXT-R']}
                style={{
                  border: 0,
                  background: 'transparent',
                  cursor: isProject ? 'pointer' : 'default',
                  padding: 0,
                  textAlign: 'left',
                  color: isActive
                    ? colorsOptions.greys['grey-90']
                    : colorsOptions.greys['grey-40']
                }}
              >
                {row.original.name}
              </RowText>
            }
            secondaryText={
              <RowTextContainer>
                {isFolder && (
                  <RowText
                    weight={textStyleWeightOptions['TEXT-500']}
                    size={textStyleSizeOptions['TEXT-XS']}
                  >
                    {formatMessage(
                      {
                        id: 'projects_items',
                        defaultMessage: '{entries} items'
                      },
                      { entries: row.original.items }
                    )}
                  </RowText>
                )}
                {isProject && isSearching && (
                  <ProjectPathCell projectId={row.original.id} />
                )}
              </RowTextContainer>
            }
          />
          {!isActive && (
            <BadgesComponent
              variant={badgesVariantOptions.HIGHLIGHTED}
              color={badgeColorOptions.DISABLED}
              size={badgeSizeOptions.SMALL}
            >
              {formatMessage({ id: 'inactive' })}
            </BadgesComponent>
          )}
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT,
      isFlexible: true,
      minWidth: 240
    }
  },
  {
    id: projectTreeSortByOptions.LAST_ACTIVITY,
    accessorKey: 'lastActivity',
    header: formatMessage({
      id: 'projects_lastActivity',
      defaultMessage: 'Last Activity'
    }),
    size: tableColumnSizeOptions.M,
    cell: ({ row }) => (
      <TableRowCell>
        <TableRowText
          mainText={
            <RowText
              weight={textStyleWeightOptions['TEXT-500']}
              size={textStyleSizeOptions['TEXT-S']}
              color={
                row.original.active
                  ? colorsOptions.greys['grey-90']
                  : colorsOptions.greys['grey-40']
              }
            >
              {row.original.lastActivityLabel}
            </RowText>
          }
        />
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'projectSettings',
    header: null,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: ({ row }) => {
      const isProject = row.original.type === projectTreeItemTypeOptions.PROJECT

      if (!isProject) return null

      return (
        <TableRowCell>
          <ButtonComponent
            variant={buttonVariantOptions.GHOST}
            buttonType={buttonTypeOptions['ICON-ONLY']}
            onClick={() => isProject && onOpenProjectSettings(row.original.id)}
          >
            <Settings
              style={{
                fill: row.original.active
                  ? colorsOptions.greys['grey-90']
                  : colorsOptions.greys['grey-40'],
                strokeWidth: 0
              }}
            />
          </ButtonComponent>
        </TableRowCell>
      )
    },
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    accessorKey: 'contextMenu',
    header: null,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: ({ row }) => (
      <TableRowCell>
        <CompanyProjectsContextMenu
          item={row.original}
          userPermissions={userPermissions}
          // project handlers
          onCopyProject={onCopyProject}
          onDeactivateItem={onDeactivateItem}
          onMoveItem={onMoveItem}
          // folder handlers
          onCreateProject={onCreateProject}
          onCreateFolder={onCreateFolder}
          onRenameFolder={onRenameFolder}
        />
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    id: 'select',
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: ({ row }) => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        <CheckboxComponent.Root
          size={checkboxSizeOptions.MEDIUM}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onCheckedChange={row.getToggleSelectedHandler()}
        >
          <CheckboxComponent.Indicator>
            <Confirm size={iconSizeOptions['ICON-S']} />
          </CheckboxComponent.Indicator>
        </CheckboxComponent.Root>
      </TableRowCell>
    ),
    meta: {
      header: {
        type: tableHeaderTypeOptions.CHECKBOX,
        orientation: tableHeaderOrientationOptions.CENTER,
        checkbox: {
          size: checkboxSizeOptions.LARGE
        }
      }
    }
  }
]

export const buildColumnsSkeleton = ({
  formatMessage = projectMenuFormatMessageFallback
} = {}) => [
  {
    id: 'drag',
    accessorFn: (row) => row.isDraggable,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: () => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        {renderTableCellSkeleton()}
      </TableRowCell>
    ),
    meta: {
      type: tableHeaderTypeOptions.HANDLE,
      orientation: tableHeaderOrientationOptions.CENTER
    }
  },
  {
    id: projectTreeSortByOptions.TITLE,
    accessorKey: 'name',
    header: formatMessage({ id: 'title', defaultMessage: 'Title' }),
    enableSorting: false,
    cell: renderTitleCellSkeleton,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT,
      isFlexible: true,
      minWidth: 229
    }
  },
  {
    id: projectTreeSortByOptions.LAST_ACTIVITY,
    accessorKey: 'lastActivity',
    header: formatMessage({
      id: 'projects_lastActivity',
      defaultMessage: 'Last Activity'
    }),
    size: tableColumnSizeOptions.M,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.TEXT,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'projectSettings',
    header: null,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    accessorKey: 'contextMenu',
    header: null,
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: () => <TableRowCell>{renderTableCellSkeleton()}</TableRowCell>,
    meta: {
      type: tableHeaderTypeOptions.EMPTY,
      orientation: tableHeaderOrientationOptions.LEFT
    }
  },
  {
    id: 'select',
    size: tableColumnSizeOptions.XXS,
    enableSorting: false,
    cell: () => (
      <TableRowCell orientation={tableHeaderOrientationOptions.CENTER}>
        {renderTableCellSkeleton()}
      </TableRowCell>
    ),
    meta: {
      header: {
        type: tableHeaderTypeOptions.EMPTY,
        orientation: tableHeaderOrientationOptions.CENTER
      }
    }
  }
]

export const columnsDefault = buildColumnsDefault()
export const columnsSkeleton = buildColumnsSkeleton()
